import { atom } from "jotai";
import { categoriesAtom, chartDateRangeAtom, cleanedExpensesAtom } from "@/store/global";
import { startOfMonth, endOfMonth, eachWeekOfInterval, addDays, format, startOfDay, min, max } from "date-fns";
import { id } from "date-fns/locale/id";

type CategorySummary = {
  category: string;
  total: number;
  color: string;
};

export type WeeklyExpenseSummary = {
  weekLabel: string;
  totalExpense: number;
  categories: CategorySummary[];
};

export const totalExpensesBasedOnDateAtom = atom((get) => {
  const { startDate: fromDate, endDate: toDate } = get(chartDateRangeAtom);
  const categories = get(categoriesAtom);

  const expenses = get(cleanedExpensesAtom).filter(
    (expense) => !expense.deleted && new Date(expense.date) >= fromDate && new Date(expense.date) <= toDate,
  );

  const expensesByDate: Record<string, { total: number; categories: CategorySummary[] }> = {};

  expenses.forEach((expense) => {
    const expenseDate = startOfDay(new Date(expense.date));
    const dateKey = format(expenseDate, "yyyy-MM-dd");

    if (!expensesByDate[dateKey]) {
      expensesByDate[dateKey] = { total: 0, categories: [] };
    }

    expensesByDate[dateKey].total += expense.amount;

    const existingCategory = expensesByDate[dateKey].categories.find((c) => c.category === expense.category);
    if (existingCategory) {
      existingCategory.total += expense.amount;
    } else {
      expensesByDate[dateKey].categories.push({
        category: expense.category,
        total: expense.amount,
        color: categories.find((c) => c.value === expense.category)?.color || "#000000",
      });
    }
  });

  return expensesByDate;
});

export type CategoryExpenseSummary = {
  total: number;
  percentage: string;
  scaleWidthPercentage: number; // Represents the visual width for UI components
};

export const expensesByCategoryAtom = atom((get) => {
  const { startDate: fromDate, endDate: toDate } = get(chartDateRangeAtom);
  const expenses = get(cleanedExpensesAtom).filter(
    (expense) => !expense.deleted && new Date(expense.date) >= fromDate && new Date(expense.date) <= toDate,
  );

  const expensesByCategory: Record<string, number> = {};
  let totalExpenses = 0;

  // Aggregate expenses by category
  expenses.forEach((expense) => {
    if (expensesByCategory[expense.category]) {
      expensesByCategory[expense.category] += expense.amount;
    } else {
      expensesByCategory[expense.category] = expense.amount;
    }
    totalExpenses += expense.amount; // Update total expenses
  });

  // Calculate scaled widths
  const categorySummaries: Record<string, CategoryExpenseSummary> = {};
  const percentages = Object.values(expensesByCategory).map((amount) => (amount / totalExpenses) * 100);
  const maxPercentage = Math.max(...percentages);

  // Dynamic scale calculation
  const numberOfCategories = Object.keys(expensesByCategory).length;

  Object.keys(expensesByCategory).forEach((category) => {
    const total = expensesByCategory[category];
    const percentage = (total / totalExpenses) * 100;

    // Adjust scaling to prevent the highest category from always being 100%
    const max = numberOfCategories > 1 ? 90 : 100;
    let scaledWidth = (percentage / maxPercentage) * max; // Use 90% as the max width

    // Ensure a minimum width of 5% for visibility
    scaledWidth = Math.max(5, scaledWidth);

    categorySummaries[category] = {
      total,
      percentage: percentage.toFixed(0) + "%",
      scaleWidthPercentage: scaledWidth,
    };
  });

  return categorySummaries;
});

export const monthlyExpenseByWeekAtom = atom((get) => {
  const { startDate: fromDate, endDate: toDate } = get(chartDateRangeAtom);
  const totalExpensesPerDate = get(totalExpensesBasedOnDateAtom);
  const categories = get(categoriesAtom);

  let monthStart = startOfMonth(fromDate);
  const monthEnd = endOfMonth(toDate);

  // Generate the weeks within the adjusted month range
  const weeks = eachWeekOfInterval({ start: monthStart, end: monthEnd }, { locale: id });

  return weeks.map((weekStart, index) => {
    let totalExpense = 0;

    const categoriesSummary: {
      [key: string]: {
        total: number;
        color: string;
      };
    } = {};

    weekStart = max([weekStart, monthStart]);
    const weekEnd = min([addDays(weekStart, 6), monthEnd]);

    for (let day = weekStart; day <= weekEnd && day <= monthEnd; day = addDays(day, 1)) {
      const dateKey = format(day, "yyyy-MM-dd");
      const dailyExpense = totalExpensesPerDate[dateKey];
      if (dailyExpense) {
        totalExpense += dailyExpense.total;

        dailyExpense.categories.forEach(({ category, total }) => {
          categoriesSummary[category] = {
            ...categoriesSummary[category],
            total: (categoriesSummary[category]?.total || 0) + total,
            color: categories.find((c) => c.value === category)?.color || "#000000",
          };
        });
      }
    }

    const weekLabel = `Week ${index + 1} (${format(weekStart, "MMM dd", { locale: id })} - ${format(weekEnd, "MMM dd", {
      locale: id,
    })})`;

    return {
      weekLabel,
      totalExpense,
      categories: Object.entries(categoriesSummary).map(([category, total]) => ({
        category,
        total: total.total,
        color: total.color,
      })),
    };
  });
});

export const selectedCategoryOverviewFilterAtom = atom<string | null>(null);
